import React from "react";
import { FORMAT_LABEL, STATUS_LABEL } from "@/data/reel";
import InstagramReelFrame from "@/components/InstagramReelFrame";
import TVFrame from "@/components/TVFrame";
import FacebookCarouselFrame from "@/components/FacebookCarouselFrame";

/**
 * AdCard — renders a single video sample ad (TV/Social commercial or Reel).
 * Reels render inside an Instagram phone-frame template.
 * TV/Social commercials render inside a TV bezel frame.
 * Static social ads render inside a thin silver-bordered frame.
 * Carousel social ads render inside a full Meta-style carousel frame.
 * For mobile display campaigns, use <DisplayCampaignCard /> instead.
 */
const AdCard = ({ ad, compact = false }) => {
  const isVertical = ad.format === "reel";
  const isSocial = ad.format === "social-static";
  const isCarousel = ad.format === "social-carousel";

  return (
    <div
      className="surface p-3 sm:p-6 flex flex-col w-full max-w-[470px] mx-auto"
      data-testid={`ad-card-${ad.id}`}
    >
      {isCarousel && ad.carousel ? (
        <div className="py-1 sm:py-2 flex justify-center">
          <FacebookCarouselFrame
            page={ad.carousel.page}
            body={ad.carousel.body}
            slides={ad.carousel.slides}
            ctaDefault={ad.carousel.ctaDefault}
            displayUrl={ad.carousel.displayUrl}
            testId={`ad-carousel-${ad.id}`}
          />
        </div>
      ) : isSocial ? (
        <div className="py-1 sm:py-2 flex justify-center">
          {/* Thin silver border to match the TV bezel + mobile-display slot look.
              We do NOT resize/crop the source — image renders at natural aspect
              ratio inside its own bordered frame. */}
          <div
            className="w-full rounded-md overflow-hidden p-[1.5px]"
            style={{
              background:
                "linear-gradient(180deg, #d6d6d8 0%, #b4b4b8 50%, #8e8e93 100%)",
            }}
          >
            <div className="w-full bg-[var(--af-navy-deep)] rounded-[3px] overflow-hidden">
              <img
                src={ad.src}
                alt={ad.client}
                loading="lazy"
                className="w-full h-auto block"
                data-testid={`ad-video-${ad.id}`}
              />
            </div>
          </div>
        </div>
      ) : isVertical ? (
        <div className="py-1 sm:py-2 flex justify-center">
          <InstagramReelFrame
            src={ad.src}
            poster={ad.poster}
            handle={ad.handle || "@anneflinn"}
            caption={ad.caption}
            testId={`ad-video-${ad.id}`}
          />
        </div>
      ) : (
        <div className="py-1 sm:py-2 flex justify-center">
          <TVFrame
            src={ad.src}
            poster={ad.poster}
            aspectRatio={ad.aspect || "16/9"}
            testId={`ad-video-${ad.id}`}
          />
        </div>
      )}

      <div className="mt-3 sm:mt-5 flex flex-wrap items-center gap-1.5 sm:gap-2">
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold bg-[var(--af-lime)] text-[var(--af-navy)] px-2 py-0.5 rounded-sm">
          {STATUS_LABEL[ad.status]}
        </span>
        {ad.industry && (
          <span className="text-[10px] uppercase tracking-[0.18em] font-semibold border border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 px-2 py-0.5 rounded-sm">
            {ad.industry}
          </span>
        )}
        <span className="text-[10px] uppercase tracking-[0.18em] font-semibold border border-[var(--af-cream)]/30 text-[var(--af-cream)]/80 px-2 py-0.5 rounded-sm">
          {FORMAT_LABEL[ad.format]}
        </span>
      </div>

      <div className={`font-display ${compact ? "text-lg" : "text-xl sm:text-2xl"} mt-2 sm:mt-3 leading-tight`}>
        {ad.client}
      </div>
      {!isVertical && (
        <p className="text-xs sm:text-sm text-[var(--af-cream)]/75 leading-relaxed mt-1.5 sm:mt-2">
          {ad.caption}
        </p>
      )}
    </div>
  );
};

export default AdCard;
export { AdCard };
